import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AppLifecycleManagement} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';

import * as BenchlingAppActions from '../../BenchlingAppActions';
import {BENCHLING_APPS_WEB_URL} from '../../BenchlingAppDAO';
import {openBenchlingAppDeveloperPage} from '../../lib';

const DESCRIPTION_PLACEHOLDER =
  'Describe what your app does. Anyone with access to this app will see this description in their app workspace.';

const DEFAULT_LIFECYCLE_MANAGEMENT = AppLifecycleManagement.AUTOMATIC;

// TODO (BNCH-92479): remove this component when we migrate all legacy apps
export default function CreateBenchlingAppPageLegacy(): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  async function handleCreate(): Promise<void> {
    // Prevent accidentally creating multiple apps with the same data.
    if (isSubmitting) {
      return;
    }
    try {
      setIsSubmitting(true);
      const webhookUrlCleaned = webhookUrl === '' ? null : webhookUrl;
      const benchlingApp = await BenchlingAppActions.createLegacy({
        name,
        description,
        lifecycleManagement,
        webhookUrl: webhookUrlCleaned,
      });
      notifications.success(`Created app ${benchlingApp.name}`);
      openBenchlingAppDeveloperPage(benchlingApp.id);
    } finally {
      setIsSubmitting(false);
    }
  }

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lifecycleManagement, setLifecycleManagement] = useState<AppLifecycleManagement>(
    DEFAULT_LIFECYCLE_MANAGEMENT
  );
  const [webhookUrl, setWebhookUrl] = useState('');

  return (
    <SettingsPageContainer
      onClickTitle={() => {
        navigateToURL(BENCHLING_APPS_WEB_URL);
      }}
      subtitle="Create An App"
      title="Apps"
    >
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section">
          <Row>
            <Col md={8}>
              <h4>Basic Settings</h4>
              <div className={css(styles.benchlingAppMetadataField)}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e: InputChangeEvent) => {
                    setName(e.target.value);
                  }}
                  placeholder="Name your app"
                  dataTestAttrs={{component: 'TextField', element: 'benchlingAppName'}}
                />
              </div>
              <div className={css(styles.benchlingAppMetadataField)}>
                <InputWrapper
                  id="benchlingAppDescription"
                  label="Description"
                  labelStyle={FieldLabel.STYLES.LABEL}
                >
                  <textarea
                    name="description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                    }}
                    rows={3}
                    placeholder={DESCRIPTION_PLACEHOLDER}
                    {...getTestProps({component: 'textarea', element: 'benchlingAppDescription'})}
                  />
                </InputWrapper>
              </div>
              {!BenchlingConfig.ENABLE_BENCHLING_APPS_REMOVED_LIFECYCLE_MANAGEMENT && (
                <div className={css(styles.benchlingAppMetadataField)}>
                  <RadioButtonGroup<AppLifecycleManagement>
                    title="Lifecycle Management"
                    options={[
                      {
                        label: 'Automatic',
                        value: AppLifecycleManagement.AUTOMATIC,
                        dataTestAttrs: {
                          component: 'RadioButtonSelector',
                          element: 'Automatic',
                        },
                      },
                      {
                        label: 'Manual',
                        value: AppLifecycleManagement.MANUAL,
                        dataTestAttrs: {
                          component: 'RadioButtonSelector',
                          element: 'Manual',
                        },
                      },
                    ]}
                    isVertical={true}
                    selectedValue={lifecycleManagement}
                    onChange={setLifecycleManagement}
                  />
                </div>
              )}
              <div className={css(styles.benchlingAppMetadataField)}>
                <TextField
                  label="Webhook URL"
                  value={webhookUrl}
                  onChange={(e: InputChangeEvent) => {
                    setWebhookUrl(e.target.value);
                  }}
                  placeholder="Provide a webhook URL for your app to receive events"
                  dataTestAttrs={{component: 'TextField', element: 'benchlingAppWebhookUrl'}}
                />
              </div>
              <Button
                size={ButtonSize.MEDIUM}
                buttonStyle={ButtonStyle.PRIMARY}
                onClick={handleCreate}
                text="Create"
                dataTestAttrs={{component: 'Button', key: 'Create'}}
              />
            </Col>
          </Row>
        </div>
      </div>
    </SettingsPageContainer>
  );
}
const styles = StyleSheet.create({
  benchlingAppMetadataField: {
    marginBottom: 15,
  },
});
